import type { CityConfig } from "../city-template/types";

export const baltimoreConfig: CityConfig = {
  city: "Baltimore",
  state: "Maryland",
  stateAbbr: "MD",
  slug: "baltimore",
  route: "/cities/baltimore",
  title: "Emergency Passport Baltimore MD | Expedited Passport Processing | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Baltimore, Maryland — as fast as 24 hours. Fast passport renewal and new passport applications for Baltimore, Towson, Catonsville, and Columbia residents. A+ BBB rated.",
  h1: "Emergency & Expedited Passport Services in Baltimore, MD",
  heroSubtitle: "Baltimore travelers — from the Inner Harbor to Johns Hopkins to the suburbs — trust Fast Passport Center for emergency and expedited passports as fast as 24 hours. A+ BBB rated courier service serving all of Baltimore County and Howard County.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Stunning%20aerial%20view%20of%20Baltimore%20Maryland%20Inner%20Harbor%20skyline%20at%20golden%20hour%20with%20historic%20waterfront%20buildings%2C%20modern%20glass%20skyscrapers%2C%20Federal%20Hill%20park%2C%20and%20the%20Chesapeake%20Bay%20in%20the%20distance%2C%20warm%20amber%20lighting%20reflecting%20on%20the%20water%2C%20dramatic%20clouds%2C%20professional%20cityscape%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20architectural%20details&width=1800&height=700&seq=baltimore-hero-001&orientation=landscape",
  heroImageAlt: "Aerial view of Baltimore Inner Harbor skyline at golden hour with waterfront buildings and Chesapeake Bay",
  geoPosition: "39.2904;-76.6122",
  geoPlacename: "Baltimore, Maryland",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Washington DC Passport Agency",
    address: "600 19th St NW, 1st Floor, Washington, DC 20006",
    distance: "Approximately 45–55 minutes from downtown Baltimore via I-95 S",
  },
  nearbyCitiesZips: [
    { name: "Baltimore", zips: "21201, 21202, 21205, 21206, 21207, 21209, 21210, 21211, 21212, 21213, 21214, 21215, 21216, 21217, 21218, 21222, 21223, 21224, 21225, 21226, 21227, 21228, 21229, 21230, 21231, 21233, 21234, 21236, 21237, 21239, 21244, 21250, 21251, 21252" },
    { name: "Towson", zips: "21204, 21252, 21286" },
    { name: "Catonsville", zips: "21228, 21229" },
    { name: "Columbia", zips: "21044, 21045, 21046" },
    { name: "Ellicott City", zips: "21041, 21042, 21043" },
    { name: "Glen Burnie", zips: "21060, 21061, 21062" },
    { name: "Dundalk", zips: "21222" },
    { name: "Essex", zips: "21221" },
    { name: "Parkville", zips: "21234" },
    { name: "Pikesville", zips: "21208" },
    { name: "Woodlawn", zips: "21207, 21244" },
    { name: "Arbutus", zips: "21227" },
    { name: "Lutherville", zips: "21093" },
    { name: "Timonium", zips: "21093" },
    { name: "Halethorpe", zips: "21227" },
    { name: "Lansdowne", zips: "21227" },
    { name: "Brooklyn", zips: "21225" },
    { name: "Curtis Bay", zips: "21226" },
    { name: "Rosedale", zips: "21237" },
    { name: "Middle River", zips: "21220" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier for Baltimore and surrounding metro departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-hospital-line", title: "Medical & Research Hub", desc: "Serving Johns Hopkins, University of Maryland, and MedStar travelers" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels — no trip to downtown DC" },
  ],
  internalLinks: [
    { name: "Expedited passport in Annapolis, MD", route: "/cities/annapolis" },
    { name: "Emergency passport help in Silver Spring, MD", route: "/cities/silver-spring" },
    { name: "Urgent passport services in Columbia, MD", route: "/cities/columbia" },
    { name: "Maryland passport services overview", route: "/states/maryland" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Baltimore, Maryland remotely via secure registered courier. We do not have a walk-in office in Baltimore — our registered couriers hand-deliver your documents to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Baltimore metro travelers without the long drive to downtown DC.",
  fpcOfficeDistance: "Serving Baltimore, Towson, Catonsville, Columbia, and greater Baltimore metro",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout the Baltimore metropolitan area. From the historic neighborhoods of Federal Hill and Fells Point to the medical corridors of Johns Hopkins and University of Maryland, from the suburban communities of Towson and Pikesville to the industrial zones of Sparrows Point — we serve all of Baltimore's diverse neighborhoods and surrounding counties.",
  nearbyCitiesIntro: "We serve Baltimore and nearby communities including Towson, Catonsville, Columbia, Ellicott City, Glen Burnie, Dundalk, Essex, Parkville, Pikesville, Woodlawn, Lutherville, Timonium, and all of Baltimore County and Howard County. We also serve Anne Arundel County residents who prefer our expedited service over the self-service route.",
  howItWorksIntro: "Most Baltimore travelers complete everything online and ship documents overnight. No need to drive 45–55 minutes to downtown DC or battle I-95 traffic during rush hour.",
  mistakesIntro: "Baltimore is a major business and medical travel hub with a diverse population. Here are the most common passport mistakes we see from Baltimore travelers.",
  testimonialsIntro: "Baltimore travelers — from medical professionals to harbor workers to students — trust us when their departure is days away.",
  faqIntro: "Have questions about expedited passports in Baltimore, MD? Here are the most common ones we hear from Charm City travelers.",
  statePageRoute: "/states/maryland",
  statePageName: "Maryland",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Must drive 45–55 min to downtown DC", "Full day commitment — challenging for working professionals"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Perfect for business and medical travel deadlines", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For Baltimore travelers with immediate departure needs — as fast as 24 hours. Perfect for medical conferences, research trips, and last-minute business travel.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Business and medical travel support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Baltimore adults with a passport issued within the last 15 years.",
      bullets: ["No clerk visit required for most renewals", "Mail-in or courier options", "Returned to Baltimore address"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport (First-Time)",
      description: "First-time passport application for Baltimore residents and families — including Johns Hopkins students and international researchers.",
      bullets: ["Acceptance facility scheduling", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Emergency passport replacement for Baltimore residents who lost their passport before travel.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement in 24–48 hours"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 — for Baltimore families with kids traveling internationally.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change Passport",
      description: "Passport name correction after marriage, divorce, or legal name changes for Baltimore residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Baltimore MD", description: "Urgent passport processing for Baltimore Maryland travelers with immediate departure needs." },
    { name: "Expedited Passport Renewal Baltimore MD", description: "Fast passport renewal for Baltimore Maryland residents with a previously-issued passport." },
    { name: "First-Time Passport Baltimore MD", description: "New passport application services for Baltimore Maryland residents applying for their first passport." },
    { name: "Child Passport Baltimore MD", description: "Minor passport services for Baltimore Maryland families with children under 16." },
  ],
  testimonials: [
    {
      name: "Dr. Michael R.",
      location: "Johns Hopkins, Baltimore",
      rating: 5,
      text: "Had a medical conference in Geneva in 5 days. Fast Passport Center reviewed every document, caught a photo issue, and delivered my renewed passport in 48 hours. Absolutely essential for busy medical professionals.",
      service: "Passport Renewal",
    },
    {
      name: "Lisa T.",
      location: "Fells Point, Baltimore",
      rating: 5,
      text: "Family cruise to the Caribbean with 8 days notice. Four passports processed in under a week. The agent coordinated everything — we just shipped the documents from the UPS Store on Boston Street.",
      service: "Family Passport Bundle",
    },
    {
      name: "James K.",
      location: "Towson, MD",
      rating: 5,
      text: "Business trip to Tokyo with 10 days notice. Fast Passport Center had my emergency passport ready in 3 days. No need to drive to DC — everything shipped overnight.",
      service: "Emergency Passport",
    },
    {
      name: "Angela M.",
      location: "Columbia, MD",
      rating: 5,
      text: "Lost my passport the week before my honeymoon in Italy. I was devastated. They walked me through the replacement process and had it in my hands in 48 hours. Truly saved our trip.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "baltimore-1",
      name: "Baltimore Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "900 E Fayette St",
      city: "Baltimore",
      state: "MD",
      zip: "21233",
      phone: "(410) 685-2635",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Baltimore, near the Inner Harbor",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary downtown acceptance facility. Appointments required — book 1–2 weeks in advance. Limited parking — plan for public transit or rideshare.",
      icon: "ri-mail-line",
    },
    {
      id: "baltimore-2",
      name: "Towson Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 W Chesapeake Ave",
      city: "Towson",
      state: "MD",
      zip: "21204",
      phone: "(410) 296-2694",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Towson, approximately 8 miles north of Baltimore",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Towson University students and northern Baltimore County residents. Call to confirm current passport acceptance hours.",
      icon: "ri-mail-line",
    },
    {
      id: "baltimore-3",
      name: "Catonsville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1000 Ingleside Ave",
      city: "Catonsville",
      state: "MD",
      zip: "21228",
      phone: "(410) 788-4600",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Catonsville, approximately 10 miles west of downtown Baltimore",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for western Baltimore County and UMBC area residents. Appointments fill quickly during summer travel season.",
      icon: "ri-mail-line",
    },
    {
      id: "baltimore-4",
      name: "Columbia Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8805 Centre Park Dr",
      city: "Columbia",
      state: "MD",
      zip: "21045",
      phone: "(410) 730-2220",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Columbia Town Center, approximately 20 miles southwest of Baltimore",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serving Howard County residents. Good alternative for Ellicott City, Laurel, and Jessup families.",
      icon: "ri-mail-line",
    },
    {
      id: "baltimore-5",
      name: "Baltimore County Clerk's Office",
      type: "County Clerk Passport Acceptance",
      address: "11101 Gilroy Rd",
      city: "Hunt Valley",
      state: "MD",
      zip: "21031",
      phone: "(410) 887-2607",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Hunt Valley, approximately 15 miles north of downtown Baltimore",
      website: "https://www.baltimorecountymd.gov/",
      websiteLabel: "Baltimore County",
      notes: "County-level facility with full passport acceptance. Extended hours compared to post office locations. Accessible via I-83.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited DC slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 45–55 min drive to downtown DC", fpc: "No — ship from Baltimore" },
    { feature: "Business Travel Support", agency: "No flexibility for corporate needs", fpc: "Yes — we handle corporate cases" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
  ],
  mistakes: [
    {
      icon: "ri-time-line",
      title: "Waiting Until the Last Minute",
      description: "Baltimore's busy medical and business professionals often discover passport needs just weeks before departure. Routine processing takes 6–13 weeks. Start expedited service at least 6 weeks before your trip.",
    },
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Local photo services in Baltimore sometimes produce photos that meet general ID requirements but not passport standards. Passport photos must be exactly 2x2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 rejection reasons at Baltimore and Baltimore County acceptance facilities.",
    },
    {
      icon: "ri-map-pin-2-line",
      title: "Not Booking the Acceptance Facility Early",
      description: "Baltimore Post Office appointments fill up fast, especially during summer and holiday travel seasons. Book 2–3 weeks ahead.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Government fees change periodically. Always verify current fees before submitting — incorrect payment means starting over.",
    },
    {
      icon: "ri-time-line",
      title: "Underestimating Routine Processing Time",
      description: "Baltimore residents often assume 4–6 weeks is enough. Routine processing takes 6–13 weeks. If your travel is within 10 weeks, use expedited service.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes — from your home in Baltimore, Towson, or Columbia." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS or FedEx location near downtown Baltimore or the suburbs." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy — no rejections for small errors." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Baltimore address — trackable every step of the way." },
  ],
  faqs: [
    { q: "How fast can I get an emergency passport in Baltimore, MD?", a: "Fast Passport Center offers emergency passport processing in Baltimore, Maryland in as little as 24–48 hours. Our registered couriers hand-deliver your documents to the Washington DC Passport Agency (45–55 minutes from downtown Baltimore) — without you needing to make the drive or fight I-95 traffic." },
    { q: "Does Baltimore have its own passport agency?", a: "No — neither Baltimore nor the state of Maryland has a regional passport agency. The closest facility is the Washington DC Passport Agency, approximately 45–55 minutes from downtown Baltimore via I-95. Fast Passport Center's courier service means Baltimore residents never need to make that drive." },
    { q: "Can I get expedited passport services if I live in Towson or Columbia?", a: "Absolutely. Fast Passport Center serves all of the Baltimore metropolitan area including Towson, Columbia, Catonsville, Ellicott City, Glen Burnie, Dundalk, Essex, Parkville, and all of Baltimore County and Howard County. Our service works the same way — complete your application online, ship your documents, and we handle the rest." },
    { q: "How much does an expedited passport cost in Baltimore, MD?", a: "Government fees are fixed: $130 for a first-time passport book or $130 + $60 expedite for a renewed adult passport. Fast Passport Center's service fees vary by processing speed. Contact us for a quote based on your departure timeline." },
    { q: "What airports do Baltimore travelers use for international flights?", a: "Most Baltimore residents fly internationally through Baltimore-Washington International Airport (BWI), approximately 15 minutes south of downtown. Washington Dulles (IAD) and Reagan National (DCA) offer additional international connections. Philadelphia International (PHL) is also accessible for some travelers. Always verify your passport is valid for at least 6 months past your return date." },
    { q: "Can I get a passport for my whole family in Baltimore?", a: "Yes. Fast Passport Center handles multi-passport cases for families in Baltimore, Towson, Columbia, and surrounding areas. Whether you need child passports, renewals, or first-time applications for the whole family, we coordinate the process to ensure everyone is ready to travel." },
    { q: "Where is the closest passport acceptance facility to downtown Baltimore?", a: "The Baltimore Main Post Office at 900 E Fayette St is the primary downtown acceptance facility. The Towson Post Office at 101 W Chesapeake Ave serves northern Baltimore County. The Catonsville Post Office at 1000 Ingleside Ave serves western suburbs. All require appointments — book 1–2 weeks ahead." },
    { q: "How do I know if I need expedited or routine passport processing?", a: "If your travel is within 10 weeks, use expedited service. If your travel is within 2–3 weeks, use emergency expedited service. Routine processing takes 6–13 weeks and is only suitable for travel more than 3 months away. Baltimore residents should plan ahead, especially for medical conferences and summer cruises." },
  ],
  photoLocations: [
    { name: "Walgreens — Downtown Baltimore", address: "200 N Charles St, Baltimore, MD 21201", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Inner Harbor", address: "100 E Pratt St, Baltimore, MD 21202", hours: "Mon–Sun: 8AM–10PM" },
    { name: "UPS Store — Towson", address: "1000 York Rd, Towson, MD 21204", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "FedEx Office — Columbia", address: "8860 Columbia 100 Pkwy, Columbia, MD 21045", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    { name: "MVA — Baltimore City", address: "5425 Reisterstown Rd, Baltimore, MD 21215", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Serves Baltimore City and northwest Baltimore County. REAL ID processing available.", featured: true },
    { name: "MVA — Glen Burnie", address: "6601 Ritchie Hwy, Glen Burnie, MD 21062", hours: "Mon–Fri: 8:30AM–4:30PM", note: "Flagship MVA location for the Baltimore metro area. Full service including REAL ID.", featured: false },
  ],
  airports: [
    {
      code: "BWI",
      name: "Baltimore-Washington International Thurgood Marshall",
      distance: "15–20 minutes from downtown Baltimore",
      address: "7050 Friendship Rd, Baltimore, MD 21240",
      note: "Primary international departure airport for Baltimore travelers. Direct flights to London, Cancun, Reykjavik, and the Caribbean. Southwest Airlines hub with extensive domestic connections.",
      flightUrl: "https://www.bwiairport.com/",
      flightLabel: "BWI Flight Info",
      featured: true,
    },
    {
      code: "DCA",
      name: "Reagan National (Washington DC)",
      distance: "50–60 minutes from downtown Baltimore",
      address: "2401 Smith Blvd, Arlington, VA 22202",
      note: "Secondary option for international connections via domestic hubs. Major business travel airport.",
      flightUrl: "https://www.flyreagan.com/",
      flightLabel: "DCA Flight Info",
      featured: false,
    },
    {
      code: "IAD",
      name: "Dulles International (Washington DC)",
      distance: "60–70 minutes from downtown Baltimore",
      address: "1 Saarinen Cir, Dulles, VA 20166",
      note: "Alternative for long-haul international flights to Europe and Asia. United Airlines hub.",
      flightUrl: "https://www.flydulles.com/",
      flightLabel: "IAD Flight Info",
      featured: false,
    },
  ],
};